import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { getVmsSources, proxyVmsVendorSource } from '@vlabs/api/point-api/client.ts';
import {
  MaximizeFrameIcon, MinimizeFrameIcon, SettingsIcon, HistoryIcon,
} from '@vlabs/icons';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import {
  Control, useModal, ButtonSwitch, ButtonSwitchGroup,
} from '@vlabs/uikit';
import cn from 'classnames';
import { addHours, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { WsVideoPlayer } from '@vlabs/shared/video-player/WsVideoPlayer';

import { SettingsModal } from './SettingsModal';
import { VideoPlayer } from './video-player/VideoPlayer';
import st from './VideoWall.module.sass';

const TAB_TYPES = {
  realTime: 'realTime',
  withTime: 'withTime',
};

function HLSVideo({ settings, updateSettings, setIsError }) {
  const { t } = useTranslation();
  const [videoData, setVideoData] = useState(undefined);
  const vmsSource = useMemo(() => settings?.vmsSource, [settings]);
  const startTime = useMemo(() => settings?.startTime, [settings]);
  const [tab, setTab] = useState(startTime ? TAB_TYPES.withTime : TAB_TYPES.realTime);

  useEffect(() => {
    if (!vmsSource?.vendor_uuid) {
      setIsError(true);
      return;
    }

    async function playVideo() {
      try {
        let params = {};

        if (startTime) {
          const starttime = new Date(startTime).getTime() + new Date().getTimezoneOffset() * 60000;
          const endtime = addHours(starttime, 1);
          const formattedStartTime = format(starttime, 'dd.MM.yyyy HH:mm:ss');
          const formattedEndTime = format(endtime, 'dd.MM.yyyy HH:mm:ss');

          if (vmsSource.vendor === 'videointellect') {
            params = {
              starttime: formattedStartTime,
            };
          } else {
            params = {
              mode: 'archive',
              isForward: 'true',
              speed: 1,
              sound: 'off',
              starttime: formattedStartTime,
              endtime: formattedEndTime,
            };
          }
        }

        const { data: m3u8 } = await proxyVmsVendorSource(vmsSource.vendor, vmsSource.vendor_uuid, { params });

        setVideoData(m3u8);
      } catch (e) {
        setIsError(true);
        toast.error(t('vms:api.error.hls'));
      }
    }

    playVideo();
  }, [vmsSource, startTime, setIsError]);

  const handleRealTimeClick = () => updateSettings({ startTime: null });

  const handleDatetimeChange = (datetime) => {
    updateSettings({ startTime: datetime?.[0] || null });
  };

  return (
    <div className={st.HLSVideo__Root}>
      <div className={st.HLSVideo__VideoContainer}>
        {!!videoData?.hls && (
          <VideoPlayer
            autoPlay={!settings?.startTime}
            height
            isArchive={Boolean(settings?.startTime)}
            src={videoData.hls}
          />
        )}
        {!!videoData?.ws && (
          <WsVideoPlayer
            autoPlay={!settings?.startTime}
            wsUrl={videoData.uiWsUrl}
          />
        )}
      </div>

      <div className={st.HLSVideo__Toolbar}>
        <ButtonSwitchGroup
          itemClassName={st.ButtonToggle__Item}
          onChange={setTab}
          testId="mapStreamSwitch"
          value={tab}
        >
          <ButtonSwitch value={TAB_TYPES.realTime}>
            <Control.Button
              className={st.State}
              onClick={handleRealTimeClick}
            />
          </ButtonSwitch>
          <ButtonSwitch value={TAB_TYPES.withTime}>
            <HistoryIcon />
          </ButtonSwitch>
        </ButtonSwitchGroup>

        {tab === TAB_TYPES.withTime && (
          <Control.DateInput
            enableTime
            onChange={handleDatetimeChange}
            value={settings?.startTime || null}
          />
        )}
      </div>
    </div>
  );
}

function VideoPanel({ settings, updateSettings }) {
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [videoViewFormat, setVideoViewFormat] = useState('cover');

  const shouldUseHLS = useMemo(() => (
    settings?.vmsSource?.vendor_uuid && settings?.vmsSource?.luna_name === settings?.value
  ), [settings?.vmsSource, settings?.value]);

  const isEmpty = useMemo(() => (
    !settings?.preview?.live?.uiUrl && !settings?.vmsSource
  ), [settings?.preview?.live?.uiUrl, settings?.vmsSource]);

  useEffect(() => {
    if (!settings?.value) return;
    setIsError(false);

    if (settings?.vmsSource && settings?.vmsSource.luna_name !== settings?.value) {
      updateSettings({ vmsSource: undefined });
    }

    if (!settings?.vmsSource) {
      setIsLoading(true);
      getVmsSources()
        .then(({ vms_sources }) => {
          const matchedSources = vms_sources.filter(({ luna_name }) => luna_name === settings?.value);
          const macroscopSource = matchedSources.find(({ vendor }) => vendor === 'macroscop');

          const selectedSource = macroscopSource || matchedSources[0] || null;

          if (selectedSource) {
            updateSettings({ vmsSource: selectedSource });
          }
        })
        .catch(() => toast.error(t('vms:api.error.get vendor')))
        .finally(() => setIsLoading(false));
    }
  }, [settings?.value, settings?.vmsSource]);

  if (isEmpty) {
    return (
      <div className={st.EmptyPanel}>
        <h4>{t('vms:video wall.empty panel')}</h4>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={st.EmptyPanel}>
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={st.EmptyPanel}>
        <h4>{t('vms:video wall.error panel')}</h4>
      </div>
    );
  }

  if (shouldUseHLS) {
    return (
      <HLSVideo
        setIsError={setIsError}
        settings={settings}
        updateSettings={updateSettings}
      />
    );
  }

  return (
    <React.Fragment>
      <img
        alt="Video stream"
        className={cn(st.Video, {
          [st.Video_contain]: videoViewFormat === 'contain',
        })}
        data-testid="VideoContain"
        onError={() => setIsError(true)}
        src={settings?.preview?.live?.uiUrl}
      />
      <div className={st.SizeButtonWrapper}>
        <Control.Button
          className={st.SizeButton}
          data-testid="SizeButton"
          icon={videoViewFormat === 'contain' ? <MaximizeFrameIcon /> : <MinimizeFrameIcon />}
          kind="secondary"
          onClick={() => setVideoViewFormat(videoViewFormat === 'contain' ? 'cover' : 'contain')}
          variant="dimmed"
        />
      </div>
    </React.Fragment>
  );
}

const saveSettings = (settings) => {
  localStorage.setItem('settingsVideoWall', JSON.stringify(settings));
};

export function VideoWall() {
  const modal = useModal();

  const defaultSettings = JSON.parse(localStorage.getItem('settingsVideoWall'));
  const [settings, setSettings] = useState(defaultSettings || {
    layoutType: '2x2',
  });

  const updatePanelSettings = useCallback((screenKey, newValues) => {
    setSettings((prev) => {
      const updated = {
        ...prev,
        [screenKey]: {
          ...(prev[screenKey] || {}),
          ...newValues,
        },
      };
      saveSettings(updated);
      return updated;
    });
  }, []);

  const handleSubmitModal = (values) => {
    saveSettings(values);
    setSettings(values);
    modal.close();
  };

  const videoPanels = useMemo(() => {
    const [rows, columns] = settings.layoutType.split('x').map((item) => +item);
    const screenKeys = Array.from({ length: rows * columns }, (_, i) => `${i + 1}Screen`);

    return (

      <React.Fragment>
        {
          !!screenKeys.length && screenKeys.map((key) => (
            <div
              className={st.VideoPanel}
              data-testid="VideoPanel"
              key={key}
            >
              <VideoPanel
                screenKey={key}
                settings={settings[key]}
                updateSettings={(newValues) => updatePanelSettings(key, newValues)}
              />
            </div>
          ))
        }
      </React.Fragment>
    );
  }, [settings, updatePanelSettings]);

  return (
    <Page>
      {modal.wrap(
        <SettingsModal
          defaultValues={settings}
          onSubmit={handleSubmitModal}
        />,
      )}
      <PageContent
        className={cn(st.Page, {
          [st.Page_vertical]: settings.layoutType === '1x2',
          [st.Page_horizontal]: settings.layoutType === '2x1',
        }, st[`Page_${settings?.layoutType}`])}
      >
        <div className={st.SettingsButtonPanel}>
          <Control.Button
            className={st.SettingsButton}
            data-testid="SettingsButton"
            icon={<SettingsIcon />}
            kind="secondary"
            onClick={modal.open}
          />
        </div>

        {videoPanels}
      </PageContent>
    </Page>
  );
}
